import {Box, Button, Checkbox, Input, Typography} from '@viptech/react-components'
import _ from 'lodash'
import {useSnackbar} from 'notistack'
import {useEffect, useRef, useState} from 'react'
import {ListFacialRecognitionDTO} from 'src/common/models/facial-recognition/ListFacialRecognitionDTO'
import {VerifyUserAndCompanyPermission} from 'src/common/utils/ValidatePermissions'
import {isEmailLoginValid} from 'src/utilities/regexValidation'
import Dropdown from '../../../../../../../../components/dropdown/Dropdown'
import {DropdownItem} from '../../../../../../../../components/dropdown/DropdownProps'
import api, {CreateActions} from '../../../../../../../../services/api'
import {handleFacePermission, ListTypeDetectionColorEnum} from '../../../../../../../../utilities/enums/listTypeDetectionColorEnum'
import handleErrorWithSnackbar from '../../../../../../../../utilities/handleErrorWithSnackbar'
import {useCameraConfigContext} from '../../../../../../context/CameraConfigContext'
import {StepperModalActionsButtonContainer} from '../../../styles'
import Card from '../../common/components/Card'
import {DispatchAllAndBlockUnknownIds, EnumLprIntegrations, skipThirdStep} from '../Constants'
import {ContainerActions, ScrollContentActions} from './CreateMultipleActionsBase'
import {CreateMultipleActionsProps, MultipleActionsType} from './types/CreateMultipleActionsTypes'

const dropdownItemNoneSelected: DropdownItem = {
  id: '',
  label: '',
}

const enumServerLabel = {
  'One Portaria': 'Setor',
  Moni: 'Partição',
  Telegram: 'Id do chat',
  'E-mail': 'Enviar para',
  'Receita Federal - Sivana': 'Código do ponto',
  'Polícia Rodoviária do Paraná - Harpia': 'Identificador da câmera',
  WhatsApp: 'Link do chat',
  'Plataforma Áspide': 'Código do equipamento',
  'Plataforma Muralha Paulista': 'Código da câmera',
}

const lprIntegrations = EnumLprIntegrations.map((it) => it.id)

const enumLists = ['Reconhecimento Facial', 'Facial', 'Leitura de Placas', 'Placa']

const CreateMultipleActions = (props: CreateMultipleActionsProps) => {
  const {finishFunction, returnFunction, chosen, outputServersItems, actions, updateTable, isEdit} = props
  const {camera} = useCameraConfigContext()
  const {enqueueSnackbar} = useSnackbar()

  const [selectedServer, setSelectedServer] = useState(dropdownItemNoneSelected)
  const [selectedList, setSelectedList] = useState<DropdownItem>(dropdownItemNoneSelected)

  const [multipleActionsList, setMultipleActionsList] = useState<MultipleActionsType[]>(actions)
  const [partition, setPartition] = useState(camera?.configuration.partition || '')
  const [showActions, setShowActions] = useState(false)

  const [telegramChatId, setTelegramChatId] = useState('')
  const [whatsappLink, setWhatsappLink] = useState('')

  const [emailToDispatchAction, setEmailToDispatchAction] = useState('')

  const [faceListsItems, setFaceListsItems] = useState<DropdownItem[]>([
    {id: '-1', label: 'Faces desconhecidas'},
    {id: '-2', label: 'Todas as faces'},
  ])
  const showFaceOrPlatesLists = useRef(false)
  const [platesListsItems, setPlatesListsItems] = useState<DropdownItem[]>([
    {id: '-1', label: 'Placas desconhecidas'},
    {id: '-2', label: 'Todas as placas'},
  ])

  const removedLinksMatrix = useRef(new Map<string, boolean>())

  const [hasLoaded, setHasLoaded] = useState(false)

  const [outputServers, setOutputServers] = useState(outputServersItems)

  const [checkPlateIsRobbed, setCheckPlateIsRobbed] = useState(false)
  const [fetchInfoFromSelected, setFetchInfoFromSelected] = useState<DropdownItem>(dropdownItemNoneSelected)
  const [shouldDispatch, setShouldDispatch] = useState(false)

  const [isLoading, setIsLoading] = useState(false)
  const hasFetchInfoSinespPermission = VerifyUserAndCompanyPermission([camera?.companyId!], 'fetch-sinesp:create')

  const [isLoadingGetLists, setIsLoadingGetLists] = useState(false)
  const [integrationCode, setIntegrationCode] = useState('')

  useEffect(() => {
    if (chosen?.detectionTypeChosen === 'Reconhecimento Facial' || chosen?.detectionTypeChosen === 'Facial') {
      getFaceLists()
      showFaceOrPlatesLists.current = true
    }
    if (chosen?.detectionTypeChosen === 'Leitura de Placas' || chosen?.detectionTypeChosen === 'Placa') {
      getPlateLists()
      showFaceOrPlatesLists.current = true
    }
    setHasLoaded(true)
  }, [])

  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [isValidInput, setIsValidInput] = useState<boolean>(true)

  const handleValidateEmailMask = (email: string) => {
    if (isEmailLoginValid(email)) {
      setEmailToDispatchAction(email)
      setIsValidInput(true)
      setErrorMessage(undefined)
      return
    }
    setIsValidInput(false)
    setErrorMessage('Insira um e-mail válido')
  }

  const handleValidateWhatsLink = (link: string) => {
    if (link.includes('https://') && link.includes('whatsapp')) {
      setWhatsappLink(link)
      setIsValidInput(true)
      setErrorMessage(undefined)
      return
    }
    setIsValidInput(false)
    setErrorMessage('Insira um link válido')
  }

  const handleNextInputComponent = () => {
    if (selectedServer.label === 'One Portaria' || selectedServer.label === 'Moni') {
      return (
        <Input.Root
          labelFontWeight='400'
          placeholder={enumServerLabel[selectedServer.label as keyof typeof enumServerLabel]}
          label={enumServerLabel[selectedServer.label as keyof typeof enumServerLabel]}
          value={partition}
          defaultValue={camera?.configuration.partition}
          onChange={(e) => setPartition(e.target.value)}
        />
      )
    }

    if (selectedServer.label === 'Telegram') {
      return (
        <Box>
          <Input.Root
            labelFontWeight='400'
            placeholder={''}
            label={'Id do chat'}
            onChange={(e) => setTelegramChatId(e.target.value)}
            mask={(value) => {
              if (value.length <= 19 && value.match(/^-?([0-9]+)?$/)) return value
              return telegramChatId
            }}
          />
        </Box>
      )
    }

    if (selectedServer.label === 'WhatsApp') {
      return (
        <Box marginBottom={!isValidInput ? '20px' : '0px'}>
          <Input.Root
            error={!isValidInput}
            errorMessage={errorMessage}
            labelFontWeight='400'
            placeholder={''}
            label={'Link de convite do chat'}
            onChange={(e) => handleValidateWhatsLink(e.target.value)}
            value={whatsappLink}>
            <Input.SingleError message={errorMessage}></Input.SingleError>
          </Input.Root>
        </Box>
      )
    }
    if (selectedServer.label === 'E-mail') {
      return (
        <Box marginBottom={!isValidInput ? '20px' : '0px'}>
          <Input.Root
            error={!isValidInput}
            errorMessage={errorMessage}
            labelFontWeight='400'
            label={'E-mail'}
            onChange={(e) => handleValidateEmailMask(e.target.value)}>
            <Input.SingleError message={errorMessage}></Input.SingleError>
          </Input.Root>
        </Box>
      )
    }
    if (selectedServer.id !== 'Spia' && lprIntegrations.includes(selectedServer.id)) {
      return (
        <Box>
          <Input.Root
            labelFontWeight='400'
            label={EnumLprIntegrations.find((it) => it.id === selectedServer.id)?.inputLabel ?? ''}
            onChange={(e) => setIntegrationCode(e.target.value)}
          />
        </Box>
      )
    }
  }

  function clearFields() {
    setSelectedServer(dropdownItemNoneSelected)
    setSelectedList(dropdownItemNoneSelected)
    setFetchInfoFromSelected(dropdownItemNoneSelected)
    setPartition(camera?.configuration.partition || '')
    setTelegramChatId('')
    setEmailToDispatchAction('')
    setIntegrationCode('')
    setWhatsappLink('')
  }

  const inputValue = () => {
    if (selectedServer.label === 'Sigma Cloud') return ''
    if (selectedServer.label === 'Telegram') return telegramChatId
    if (selectedServer.label === 'WhatsApp') return whatsappLink
    if (selectedServer.id === 'Spia' || !partition) return ''
    if (selectedServer.label === 'E-mail') return emailToDispatchAction
    if (lprIntegrations.includes(selectedServer.id)) return integrationCode
    return partition
  }

  const handleAddMultipleActions = () => {
    const telegramIsSelected = selectedServer.label === 'Telegram'
    const emailIsSelected = selectedServer.label === 'E-mail'
    const whatsappIsSelected = selectedServer.label === 'WhatsApp'
    const lprIntegrationSelected = EnumLprIntegrations.find((it) => it.id === selectedServer.id)
    const colorOnList = faceListsMemory.current.find((it) => it.id === +selectedList.id)?.listColor

    const action = {
      outputId: lprIntegrationSelected ? lprIntegrationSelected.lprIntegrationId : +selectedServer.id,
      label: selectedServer.label,
      inputLabel: enumServerLabel[selectedServer.label as keyof typeof enumServerLabel],
      valueInput: inputValue(),
      listId: !fetchInfoFromSelected.label ? +selectedList.id : undefined,
      listName: !fetchInfoFromSelected.label ? selectedList.label : undefined,
      telegramChatId: +telegramChatId,
      fetchInfoFrom: fetchInfoFromSelected.label,
      dispatchToEmail: emailToDispatchAction,
      integrationCode,
      listType: colorOnList ? handleFacePermission(colorOnList) : undefined,
    }

    const actionListId = action.listId
    if (!telegramIsSelected && !emailIsSelected && !whatsappIsSelected) removedLinksMatrix.current.set(`${actionListId}-${action.outputId}`, true)

    setMultipleActionsList((prev) => [...prev, action])
    clearFields()
  }

  const removeActionFromList = async (action: MultipleActionsType, indexActionToRemove: number) => {
    if (action.idToRemove) {
      try {
        await api.object.deleteAction(action.idToRemove)
        enqueueSnackbar('Ação deletada com sucesso', {
          variant: 'success',
        })
      } catch (error) {
        handleErrorWithSnackbar(enqueueSnackbar, error, 'Não foi possível deletar a ação')
      }
    }
    const newMultipleActionsList = multipleActionsList.filter((_, index) => index !== indexActionToRemove)
    const actionListId = action.listId
    removedLinksMatrix.current.set(`${actionListId}-${action.outputId}`, false)
    setMultipleActionsList(newMultipleActionsList)
  }

  useEffect(() => {
    const action = multipleActionsList.find((it) => it.outputId)
    if (action) removedLinksMatrix.current.set(`${action.listId}-${action.outputId}`, true)
  }, [])

  function handleAddObjectWithList(toAdd: MultipleActionsType): CreateActions {
    const isTelegram = toAdd.label === 'Telegram'
    const isEmail = toAdd.label === 'E-mail'
    const isWhatsapp = toAdd.label === 'WhatsApp'
    const isLprIntegrationType = EnumLprIntegrations.find((it) => it.label === toAdd.label)

    const action = {} as CreateActions

    if (isTelegram && toAdd.telegramChatId) {
      action.type = 'telegram'
      action.outputActionId = toAdd.telegramChatId
    } else if (isWhatsapp) {
      action.type = 'whatsapp'
      action.outputActionId = toAdd.valueInput
    } else if (isLprIntegrationType) {
      action.type = 'lpr-integration'
      action.outputActionId = toAdd.outputId
      action.integrationCode = toAdd.integrationCode
    } else if (isEmail) {
      action.dispatchToEmail = toAdd.dispatchToEmail
    } else if (toAdd.label) {
      action.type = 'output-integration'
      action.outputActionId = toAdd.outputId
    }

    if (toAdd.listId === DispatchAllAndBlockUnknownIds.DESCONHECIDAS) {
      action.blockUnknownByClient = true
      action.actionColor = ListTypeDetectionColorEnum.BLACKLIST
    } else if (toAdd.listId === DispatchAllAndBlockUnknownIds.TODAS) {
      action.dispatchAllPlates = true
      action.actionColor = ListTypeDetectionColorEnum.ALL
    } else if (toAdd.listId) {
      action.listIds = [toAdd.listId]
      action.actionColor = faceListsMemory.current.find((it) => it.id === toAdd.listId)?.listColor ?? ListTypeDetectionColorEnum.WHITELIST
    }

    if (toAdd.fetchInfoFrom) {
      action.checkPlateIsRobbed = true
      if (toAdd.fetchInfoFrom === 'Sinesp') action.fetchVehicleInfoFromSinesp = true
      else action.fetchVehicleInfoFromFipe = true
    }
    return action
  }

  function handleAddEmailAction(toAdd: MultipleActionsType, needListId: boolean): CreateActions {
    if (needListId) return handleAddObjectWithList(toAdd)
    return {
      dispatchToEmail: toAdd.dispatchToEmail,
    } as CreateActions
  }

  function handleAddTriggerAndOutput(toAdd: MultipleActionsType, needListId: boolean): CreateActions {
    if (needListId) return handleAddObjectWithList(toAdd)
    return {
      type: 'output-integration',
      outputActionId: toAdd.outputId,
    } as CreateActions
  }

  function handleAddTelegram(toAdd: MultipleActionsType, needListid: boolean) {
    if (needListid) return handleAddObjectWithList(toAdd)
    return {
      type: 'telegram',
      outputActionId: toAdd.telegramChatId,
    } as CreateActions
  }

  function handleAddWhatsApp(toAdd: MultipleActionsType, needListid: boolean) {
    if (needListid) return handleAddObjectWithList(toAdd)
    return {
      type: 'whatsapp',
      outputActionId: toAdd.valueInput,
    } as CreateActions
  }

  const handleSaveObjectAndActions = async () => {
    setIsLoading(true)
    const actions: CreateActions[] = []
    const needListId = enumLists.includes(chosen?.detectionTypeChosen!)
    for (const toAdd of multipleActionsList) {
      if (toAdd.idToRemove) continue
      const isTelegram = toAdd.label === 'Telegram'
      const isEmail = toAdd.label === 'E-mail'
      const isWhatsapp = toAdd.label === 'WhatsApp'

      if (isTelegram) {
        actions.push(handleAddTelegram(toAdd, needListId))
      } else if (isEmail) {
        actions.push(handleAddEmailAction(toAdd, needListId))
      } else if (isWhatsapp) {
        actions.push(handleAddWhatsApp(toAdd, needListId))
      } else {
        actions.push(handleAddTriggerAndOutput(toAdd, needListId))
      }
    }

    if (camera && partition !== camera?.configuration.partition) {
      const configuration = {..._.omit(camera.configuration, ['id', 'interestAreas', 'isOnline']), partition: partition}
      await api.camera.updateCamera(camera.id, {configuration})
    }
    const responseObject = await finishFunction(true)
    if (!responseObject) {
      setIsLoading(false)
      return
    }
    const actionsToSave = actions.filter((it) => Object.keys(it).length !== 0)
    if (actionsToSave.length === 0) return

    try {
      await api.object.putActions(responseObject.id, actionsToSave)
      enqueueSnackbar('Ações salvas com sucesso', {
        variant: 'success',
      })
    } catch (error) {
      handleErrorWithSnackbar(enqueueSnackbar, error, 'Erro ao salvar ações')
    } finally {
      updateTable()
    }
    setIsLoading(false)
  }

  const disabledButtonAddActions = () => {
    if (selectedServer.label === 'E-mail' && (!isValidInput || !emailToDispatchAction)) return true
    if (selectedServer.label === 'WhatsApp' && (!isValidInput || !whatsappLink)) return true
    if (selectedServer.label === 'Telegram' && !telegramChatId) return true
    if ((selectedServer.id === 'Sivana' || selectedServer.id === 'Harpia' || selectedServer.id === 'Aspide') && !integrationCode) return true
    if (!checkPlateIsRobbed && selectedServer.label === '') return true

    if (partition) return false
    return false
  }

  const faceListsMemory = useRef<ListFacialRecognitionDTO[]>([])

  const getFaceLists = async () => {
    try {
      if (!camera?.clientId || !camera?.companyId) return
      setIsLoadingGetLists(true)
      const response = (
        await api.facialRecognition.getList({
          filter: {
            companyIds: [camera?.companyId],
          },
        })
      ).data

      faceListsMemory.current = response.data.entities
      const faceList = response.data.entities
        .filter((list) => list.clientId === camera?.clientId || list.clientId === null)
        .map((it) => {
          return {
            id: String(it.id),
            label: it.name,
          }
        })

      const newActions = actions.map((it) => {
        const listId = it.listId
        if (listId === DispatchAllAndBlockUnknownIds.DESCONHECIDAS) return {...it, listName: 'Faces desconhecidas'}
        if (listId === DispatchAllAndBlockUnknownIds.TODAS) return {...it, listName: 'Todas as faces'}
        const label = faceList.find((list) => list.id === String(listId))?.label
        return {...it, listName: label}
      })
      setMultipleActionsList(newActions)
      setFaceListsItems((prev) => [...prev, ...faceList])
    } catch (err) {
      handleErrorWithSnackbar(enqueueSnackbar, err, 'Erro ao carregar listas cadastradas')
      return []
    } finally {
      setIsLoadingGetLists(false)
    }
  }

  const getPlateLists = async () => {
    try {
      if (!camera?.clientId || !camera?.companyId) return
      setIsLoadingGetLists(true)
      const response = (
        await api.lpr.getManyLists({
          filter: {
            companyIds: [camera?.companyId],
          },
        })
      ).data

      const plateList = response.data.entities
        .filter((list) => list.clientId === camera?.clientId || list.clientId === null)
        .map((it) => {
          return {
            id: String(it.id),
            label: it.name,
          }
        })

      const newActions = actions.map((it) => {
        const listId = it.listId
        if (listId === DispatchAllAndBlockUnknownIds.DESCONHECIDAS) return {...it, listName: 'Placas desconhecidas'}
        if (listId === DispatchAllAndBlockUnknownIds.TODAS) return {...it, listName: 'Todas as placas'}
        const label = plateList.find((list) => list.id === String(listId))?.label
        return {...it, listName: label}
      })
      setMultipleActionsList(newActions)
      setPlatesListsItems((prev) => [...prev, ...plateList])
    } catch (err) {
      handleErrorWithSnackbar(enqueueSnackbar, err, 'Erro ao carregar listas cadastradas')
      return []
    } finally {
      setIsLoadingGetLists(false)
    }
  }

  const handleGoBackFunction = () => {
    const detectionChosen = chosen?.detectionTypeChosen ?? ''
    const skipThird = skipThirdStep.includes(detectionChosen)
    if (skipThird) return returnFunction(2, 1)
    returnFunction(2)
  }

  useEffect(() => {
    if (multipleActionsList.length >= 1) setShowActions(true)
    else setShowActions(false)
  }, [multipleActionsList])

  useEffect(() => {
    const newOutputServers: DropdownItem[] = []
    outputServersItems.forEach((it) => {
      if (removedLinksMatrix.current.has(`${undefined}-${it.id}`)) {
        if (removedLinksMatrix.current.get(`${undefined}-${it.id}`) === false) {
          newOutputServers.push(it)
        }
      } else {
        newOutputServers.push(it)
      }
    })
    setOutputServers(newOutputServers)
  }, [selectedList, removedLinksMatrix.current, multipleActionsList])

  function getButtonLabel() {
    const detection = chosen?.detectionTypeChosen ?? ''
    if (skipThirdStep.includes(detection) && !multipleActionsList.length) return 'Finalizar sem vincular ação'
    return 'Finalizar'
  }

  const handleListsSelect = () => {
    return (
      <Box width='90%' display='flex' direction='column' rowGap='20px'>
        <Box>
          <Typography variant='span' size='16px' style={{fontWeight: 500}}>
            Listas cadastradas
          </Typography>
          <Typography variant='p' size='12px' color='#8E8E8E'>
            Escolha a ação que cada lista deverá executar
          </Typography>
        </Box>

        <Dropdown
          height='40px'
          fontSize='12px'
          selected={selectedList}
          placeholder='Listas'
          isLoading={isLoadingGetLists}
          items={chosen?.detectionTypeChosen?.includes('Facial') ? faceListsItems : platesListsItems}
          onChangeSelected={(e) => setSelectedList(e)}
        />
      </Box>
    )
  }

  const handleFetchInfoFrom = () => {
    const fetchInfoFrom = [
      {
        id: 'Fipe Brasil',
        label: 'Fipe Brasil',
      },
    ]

    if (hasFetchInfoSinespPermission)
      fetchInfoFrom.push({
        id: 'Sinesp',
        label: 'Sinesp',
      })

    const fetchInfoFromSelected = multipleActionsList.find((it) => it.fetchInfoFrom)
    if (fetchInfoFromSelected) return fetchInfoFrom.filter((it) => it.label === fetchInfoFromSelected.fetchInfoFrom)

    return fetchInfoFrom
  }

  const handleAllowAddNewAction = () => {
    if (!multipleActionsList.find((it) => it.inputLabel || it.label?.includes('Spia') || it.label?.includes('Harpia'))) {
      if (multipleActionsList.length > 0 && !multipleActionsList.find((it) => it.fetchInfoFrom && it.label)) return true
    } else if (multipleActionsList.find((it) => it.fetchInfoFrom && !it.label)) return true
    return false
  }

  const blockChangeCheckbox = () => {
    if (!multipleActionsList.find((it) => it.inputLabel || it.label?.includes('Spia') || it.label?.includes('Harpia'))) {
      if (multipleActionsList.length > 0 && multipleActionsList.find((it) => it.fetchInfoFrom && it.label)) {
        if (checkPlateIsRobbed) setShouldDispatch(true)
        return true
      }
    } else if (multipleActionsList.find((it) => it.fetchInfoFrom && it.label)) {
      if (checkPlateIsRobbed) setShouldDispatch(true)
      return true
    }
    return false
  }

  useEffect(() => {
    blockChangeCheckbox()
  }, [multipleActionsList, checkPlateIsRobbed])

  const handleCheckPlateIsRobbedSelect = () => {
    return (
      <Box width='90%' display='flex' direction='column' rowGap='20px'>
        <Box display='flex' direction='column' rowGap='12px'>
          <Box>
            <Typography variant='span' size='16px' style={{fontWeight: 500}}>
              Buscar Informações de Veículos
            </Typography>
            <Typography variant='p' size='12px' color='#8E8E8E'>
              Selecione a fonte em que deseja consultar as placas detectadas
            </Typography>
          </Box>

          <Dropdown
            height='40px'
            fontSize='12px'
            selected={fetchInfoFromSelected}
            placeholder='Fontes de consulta'
            items={handleFetchInfoFrom()}
            disabled={handleAllowAddNewAction()}
            onChangeSelected={(e) => setFetchInfoFromSelected(e)}></Dropdown>
        </Box>

        {!shouldDispatch && (
          <Button
            fontSize='12px'
            height='40px'
            width='300px'
            variant='outlined'
            onClick={() => handleAddMultipleActions()}
            disabled={fetchInfoFromSelected.id === ''}>
            Adicionar ação sem despacho
          </Button>
        )}
        <Box display='flex' direction='column' marginTop='10px'>
          <Checkbox
            label='Desejo despachar evento quando encontrar uma placa roubada'
            labelSize='14px'
            checked={shouldDispatch}
            onChange={() => {
              if (blockChangeCheckbox()) return
              setShouldDispatch((prev) => !prev)
            }}
            disabled={handleAllowAddNewAction()}
          />
          <Typography variant='span' size='12px' color='#8E8E8E'>
            Essa ação permite você selecionar o que deverá acontecer caso haja a detecção
          </Typography>
        </Box>
      </Box>
    )
  }

  const clearStateForAnotherCheckbox = () => {
    setSelectedList(dropdownItemNoneSelected)
    setFetchInfoFromSelected(dropdownItemNoneSelected)
  }

  const handleRobberyCheckbox = () => {
    if (chosen?.detectionTypeChosen === 'Leitura de Placas' || chosen?.detectionTypeChosen === 'Placa') {
      return (
        <Box display='flex' width='90%' justifyContent='space-between'>
          <Checkbox label='Consultar veículos' labelSize='13px' checked={checkPlateIsRobbed} onChange={() => setCheckPlateIsRobbed((prev) => !prev)} />
          <Checkbox
            label='Procurar nas listas cadastradas'
            labelSize='13px'
            checked={!checkPlateIsRobbed}
            onChange={() => {
              clearStateForAnotherCheckbox()
              setCheckPlateIsRobbed((prev) => !prev)
            }}
          />
        </Box>
      )
    }
  }

  const shouldRenderDispatchSelect = !showFaceOrPlatesLists.current || selectedList.id !== '' || shouldDispatch

  return (
    <ContainerActions>
      {hasLoaded && (
        <ScrollContentActions>
          <Box width='50%' display='flex' direction='column' rowGap='20px'>
            {handleRobberyCheckbox()}
            {showFaceOrPlatesLists.current && <Box>{!checkPlateIsRobbed ? handleListsSelect() : handleCheckPlateIsRobbedSelect()}</Box>}

            {shouldRenderDispatchSelect && (
              <Box width='90%' display='flex' direction='column' rowGap='10px'>
                <Box>
                  <Typography variant='span' size='16px' style={{fontWeight: 500}}>
                    Despachar evento
                  </Typography>
                  <Typography variant='p' size='12px' color='#8E8E8E'>
                    Selecione a forma que o evento será despachado
                  </Typography>
                </Box>

                <Dropdown
                  marginTop='10px'
                  height='40px'
                  fontSize='12px'
                  selected={selectedServer}
                  placeholder='Selecione'
                  items={outputServers}
                  onChangeSelected={(e) => setSelectedServer(e)}
                />

                {selectedServer && handleNextInputComponent()}

                <Button
                  fontSize='12px'
                  height='40px'
                  width='200px'
                  variant='outlined'
                  onClick={() => handleAddMultipleActions()}
                  disabled={disabledButtonAddActions()}>
                  {showActions ? 'Adicionar nova ação' : 'Adicionar'}
                </Button>
              </Box>
            )}
          </Box>
          <Box width='50%' display='flex' direction='column' marginRight='5px'>
            <Box
              display='flex'
              direction='column'
              rowGap='20px'
              align='flex-end'
              justify='flex-start'
              marginTop='50px'
              marginBottom='15px'
              overflow='auto'
              paddingRight='15px'>
              {showActions && (
                <>
                  {multipleActionsList?.map((it, index) => {
                    return (
                      <Card
                        initialState={true}
                        dispatchTo={it.label}
                        onClick={() => removeActionFromList(it, index)}
                        multipleActions={true}
                        customizableLabel={it.inputLabel}
                        customizableValue={it.valueInput}
                        key={index}
                        brainBox={it.brainBox}
                        listName={it.listName}
                        fetchInfoFrom={it.fetchInfoFrom}
                        listType={it.listType}
                      />
                    )
                  })}
                </>
              )}
            </Box>
          </Box>
        </ScrollContentActions>
      )}

      <StepperModalActionsButtonContainer>
        <Button
          fontSize='12px'
          height='40px'
          width='150px'
          color='#8E8E8E'
          variant='outlined'
          disabled={skipThirdStep.includes(chosen?.detectionTypeChosen!) && isEdit}
          onClick={() => handleGoBackFunction()}>
          Voltar
        </Button>
        <Button
          fontSize='12px'
          height='40px'
          disabledTextColor='#8E8E8E'
          width='200px'
          onClick={() => handleSaveObjectAndActions()}
          isLoading={isLoading}
          disabled={!(skipThirdStep.includes(chosen?.detectionTypeChosen!) || !!multipleActionsList.length)}>
          {getButtonLabel()}
        </Button>
      </StepperModalActionsButtonContainer>
    </ContainerActions>
  )
}

export default CreateMultipleActions
